import { useMediaQuery } from '@mui/material';
import React from 'react';
import Theme from '@kyruus/ui-theme';
import PropTypes from 'prop-types';
import { useProviderInteractions } from './useProviderInteractions';

/**
 * Wrapper component for provider tiles that conditionally applies mouse and keyboard event handlers
 * based on the isProviderListMapsEnabled prop.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onMouseEnter - Mouse enter event handler
 * @param {Function} props.onMouseLeave - Mouse leave event handler
 * @param {Function} props.onKeyboardFocus - Keyboard focus event handler
 * @param {Function} props.onKeyboardBlur - Keyboard blur event handler
 * @param {boolean} props.isProviderListMapsEnabled - Flag to enable/disable provider list maps
 * @param {ReactNode} props.children - Child elements to render
 * @param {Object} props.provider - Provider data object
 *
 * @returns {JSX.Element}
 */
export const ProviderTileWrapper = ({
  onProviderHover,
  onKeyboardFocus,
  onKeyboardBlur,
  isProviderListMapsEnabled,
  children,
  provider,
  ...props
}) => {
  const isScreenMediumOrAbove = useMediaQuery(
    `(min-width:${Theme.screen_medium})`
  );

  const {
    handleProviderKeyboardFocus,
    handleProviderKeyboardBlur,
    handleProviderMouseEnter,
    handleProviderMouseLeave,
    handleKeyDown
  } = useProviderInteractions({
    onProviderHover,
    onKeyboardFocus,
    onKeyboardBlur,
    provider
  });

  return isProviderListMapsEnabled && isScreenMediumOrAbove ? (
    <div
      onMouseEnter={handleProviderMouseEnter(provider)}
      onMouseLeave={handleProviderMouseLeave(provider)}
      onFocus={handleProviderKeyboardFocus(provider)}
      onBlur={handleProviderKeyboardBlur}
      onKeyDown={handleKeyDown}
      tabIndex="0"
      role="button"
      aria-label="Provider tile - press Enter to highlight on map"
      {...props}
    >
      {children}
    </div>
  ) : (
    <div {...props}>{children}</div>
  );
};

ProviderTileWrapper.displayName = 'ProviderTileWrapper';

ProviderTileWrapper.propTypes = {
  onProviderHover: PropTypes.func,
  onKeyboardFocus: PropTypes.func,
  onKeyboardBlur: PropTypes.func,
  isProviderListMapsEnabled: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  provider: PropTypes.object.isRequired
};
